import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { KeyRound, Loader2, Lock, RotateCcw, Sparkles } from "lucide-react";
import { resetPassword } from "../store/authSlice";
import { ROUTES } from "../config/constants";
import PasswordInput from "../components/PasswordInput";

const INVALID_LINK_MESSAGE =
    "This reset link is invalid or has expired. Please request a new password reset link.";

const getPasswordFieldError = (error) => {
    const detail = error?.data?.detail;
    const message =
        typeof detail === "string"
            ? detail
            : error?.message || "Please check your password and try again.";

    if (/8 characters|short|length/i.test(message)) {
        return "Password must be at least 8 characters.";
    }

    return "Please check your password and try again.";
};

const ResetPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { resetPasswordStatus } = useSelector((state) => state.auth);

    const token = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("token")?.trim() || "";
    }, [location.search]);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [hasInvalidToken, setHasInvalidToken] = useState(false);

    const isSubmitting = resetPasswordStatus === "loading";
    const isInvalidLink = hasInvalidToken || !token;

    const validateForm = () => {
        const errors = {};

        if (!newPassword) {
            errors.newPassword = "Please enter a new password.";
        } else if (newPassword.length < 8) {
            errors.newPassword = "Password must be at least 8 characters.";
        }

        if (!confirmPassword) {
            errors.confirmPassword = "Please confirm your new password.";
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = "Passwords do not match.";
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitting || isInvalidLink || !validateForm()) return;

        try {
            await dispatch(resetPassword({ token, newPassword })).unwrap();
            toast.success("Password updated successfully.");
            navigate(ROUTES.LOGIN);
        } catch (error) {
            if (error?.status === 400) {
                setHasInvalidToken(true);
                return;
            }

            if (error?.status === 422) {
                setFieldErrors((currentErrors) => ({
                    ...currentErrors,
                    newPassword: getPasswordFieldError(error),
                }));
                return;
            }

            toast.error("Something went wrong. Please try again.");
        }
    };

    const clearFieldError = (fieldName) => {
        if (!fieldErrors[fieldName]) return;

        setFieldErrors((currentErrors) => ({
            ...currentErrors,
            [fieldName]: undefined,
        }));
    };

    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center bg-[#F8F8FE] px-3 py-8 sm:px-6 lg:px-8">
            <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.95fr_1.05fr]">
                <div className="hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 p-8 text-white lg:flex lg:flex-col lg:justify-between">
                    <div>
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                            <Sparkles size={24} aria-hidden="true" />
                        </div>
                        <h1 className="mt-8 text-3xl font-bold leading-tight">
                            Create a new password
                        </h1>
                        <p className="mt-4 max-w-sm text-sm leading-6 text-indigo-50">
                            Secure your account and return to your OptiMind
                            workspace.
                        </p>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-indigo-100">
                        OptiMind account recovery
                    </p>
                </div>

                <div className="w-full p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-md">
                        <div className="mb-6 text-center sm:mb-8">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 lg:hidden">
                                <Sparkles size={24} aria-hidden="true" />
                            </div>
                            <p className="text-sm font-medium text-indigo-600">
                                Reset your password
                            </p>
                            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                                Reset Password
                            </h2>
                        </div>

                        {isInvalidLink ? (
                            <div className="space-y-4">
                                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-6 text-amber-800">
                                    {INVALID_LINK_MESSAGE}
                                </div>

                                <Link
                                    to={ROUTES.FORGOT_PASSWORD}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                >
                                    <RotateCcw size={16} aria-hidden="true" />
                                    Request a new reset link
                                </Link>

                                <Link
                                    to={ROUTES.LOGIN}
                                    className="block text-center text-sm font-semibold text-indigo-600 transition hover:text-indigo-700"
                                >
                                    Back to Login
                                </Link>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                noValidate
                                className="space-y-4"
                            >
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                        New password
                                    </label>
                                    <PasswordInput
                                        placeholder="Enter new password"
                                        value={newPassword}
                                        onChange={(event) => {
                                            setNewPassword(event.target.value);
                                            clearFieldError("newPassword");
                                        }}
                                        hasError={Boolean(
                                            fieldErrors.newPassword,
                                        )}
                                        leadingIcon={Lock}
                                    />
                                    {fieldErrors.newPassword && (
                                        <p className="mt-1.5 text-xs text-red-600">
                                            {fieldErrors.newPassword}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                        Confirm new password
                                    </label>
                                    <PasswordInput
                                        placeholder="Confirm new password"
                                        value={confirmPassword}
                                        onChange={(event) => {
                                            setConfirmPassword(
                                                event.target.value,
                                            );
                                            clearFieldError("confirmPassword");
                                        }}
                                        hasError={Boolean(
                                            fieldErrors.confirmPassword,
                                        )}
                                        leadingIcon={KeyRound}
                                    />
                                    {fieldErrors.confirmPassword && (
                                        <p className="mt-1.5 text-xs text-red-600">
                                            {fieldErrors.confirmPassword}
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                                >
                                    {isSubmitting ? (
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        <KeyRound
                                            size={18}
                                            aria-hidden="true"
                                        />
                                    )}
                                    {isSubmitting
                                        ? "Resetting..."
                                        : "Reset Password"}
                                </button>

                                <Link
                                    to={ROUTES.LOGIN}
                                    className="block text-center text-sm font-semibold text-indigo-600 transition hover:text-indigo-700"
                                >
                                    Back to Login
                                </Link>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
